/**
 * Cards Inventory view — searchable / filterable table of card lots.
 */
import { DB } from "../lib/db";
import { openLabelPrint } from "../components/labelPrint";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { sectionSort, sortRowsForView } from "../utils/table";
import { Lists } from "../lib/prefs";
import { escapeHtml, toast, $ } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar, todayISO, unitFMVForForm } from "../utils/format";
import { GAMES } from "../utils/pills";
import { onSectionChange, render } from "../state";
import { openModal, closeModal } from "../components/modal";
import { statusCellHtml } from "../components/statusCell";
import { openLotDrawer } from "../components/lotDrawer";
import { formField, readForm } from "../components/formField";
import { gradePickerHtml, wireGradePicker } from "../components/gradePicker";
import { variantSelectHtml, syncVariantSelect, wireVariantSelect } from "../components/variantSelect";
import { comboboxField, wireCombobox } from "../components/combobox";
import { nextId } from "../utils/ids";
import { SetCache } from "../lib/sets";
import { syncLotToSource } from "../lib/sync";
import { reallocateRipBasis, sourceIdIsRip } from "../lib/ripBasis";
import { BULK_CLASSES, bulkPoolLotId, bulkUnitValueFor, getBulkPool, addToBulkPool, isBulkPoolRow, type BulkClass } from "../lib/bulk";
import { exportCardsReport, openExportOptions } from "../components/printReport";
import { icon } from "../utils/icon";
import "../components/cardScanCardsight"; // registers window.openCardSightScan
import type { Row } from "../lib/types";
import "../lib/generateFromInventory";
import { queueImageCache } from "../lib/imageCache";
import { downloadCsv } from "../lib/csv";
import { normalizeSetName } from "../lib/setNames";
import { looksGraded } from "../lib/slab";
import { basisProvenance } from "../lib/basisProvenance";
import { openEbaySoldsFor } from "../lib/ebaySolds";
import { bulkSetStatus } from "../components/statusCell";

export const CardsFilters = { search: "", game: "", status: "", inventoryClass: "", showZeroQty: false };

onSectionChange.push((_prev, _next) => { CardsFilters.search = ""; _selectedCardLots.clear(); });

(window as any).CardsFilters = CardsFilters;

// Debounced search — re-render at most every ~220ms while typing so big
// inventories don't re-render on every keystroke (the \u2715 clear button
// still renders instantly).
let _cardsSearchTimer: ReturnType<typeof setTimeout> | null = null;
(window as any).cardsSearchInput = (v: string) => {
  CardsFilters.search = v;
  if (_cardsSearchTimer) clearTimeout(_cardsSearchTimer);
  _cardsSearchTimer = setTimeout(() => { _cardsSearchTimer = null; render(); }, 220);
};

/** data-col key -> raw sortable value. Drives GLOBAL sorting of the filtered
 *  dataset (before pagination) so "sort by FMV" is true across all pages. */
const CARD_SORT_ACCESSORS: Record<string, (r: Row) => any> = {
  "Lot ID":      r => r["Card Lot ID"],
  "Game":        r => r["Game"],
  "Card":        r => r["Card Name"],
  "Set":         r => r["Set"],
  "#":           r => r["Card #"],
  "Cond/Grade":  r => r["Condition/Grade"],
  "Class":       r => r["Inventory Class"],
  "Status":      r => r["Status"],
  "Qty":         r => Number(r["Qty"]) || 0,
  "Unit Basis":  r => Number(r["Unit Basis"]) || 0,
  "Total Basis": r => Number(r["Total Basis"]) || 0,
  "Unit FMV":    r => { const q = Number(r["Qty"]) || 0; const t = Number(r["Current FMV"]) || 0; return q > 0 ? t / q : t; },
  "Total FMV":   r => Number(r["Current FMV"]) || 0,
  "G/L":         r => (Number(r["Current FMV"]) || 0) - (Number(r["Total Basis"]) || 0),
  "Acq Date":    r => r["Acq Date"],
  "Source":      r => r["Source ID"],
};

/** Set of currently-checked card-lot IDs. Powers the row-actions toolbar:
 *  single-item actions (Edit / Refresh / Lookup) enable when size === 1;
 *  bulk actions (Delete / Generate Trade / Generate Sale) enable when
 *  size >= 1. Cleared on tab change. */
const _selectedCardLots = new Set<string>();

/** Single-row click opens the lot detail drawer (click = open, checkbox =
 *  select). Edit lives inside the drawer. Ignored when the click came from an
 *  inner control (checkbox, status select, history link, buttons). */
export function cardRowClick(e: MouseEvent, lotId: string): void {
  const t = e.target as HTMLElement | null;
  if (t && t.closest("a, button, input, select, label")) return;
  openLotDrawer("card", lotId);
}
(window as any).cardRowClick = cardRowClick;

/** Checkbox change — same toggle, but driven by the checkbox click rather
 *  than the row click (so they stay in sync). */
export function cardRowToggle(lotId: string, checked: boolean): void {
  if (checked) _selectedCardLots.add(lotId);
  else         _selectedCardLots.delete(lotId);
  applyCardSelectionUI();
}
(window as any).cardRowToggle = cardRowToggle;

/** Toggle every visible card row at once via the header checkbox. */
export function cardsSelectAll(checked: boolean): void {
  document.querySelectorAll<HTMLElement>("tr[data-cards-row]").forEach(tr => {
    const id = tr.getAttribute("data-cards-row") || "";
    if (!id) return;
    if (checked) _selectedCardLots.add(id);
    else         _selectedCardLots.delete(id);
  });
  applyCardSelectionUI();
}
(window as any).cardsSelectAll = cardsSelectAll;

/** Reflect _selectedCardLots in row highlights, checkbox state, and toolbar
 *  button enabled state. Called after innerHTML lands and on every change. */
function applyCardSelectionUI(): void {
  const size = _selectedCardLots.size;
  document.querySelectorAll<HTMLElement>("tr[data-cards-row]").forEach(tr => {
    const id = tr.getAttribute("data-cards-row") || "";
    const sel = _selectedCardLots.has(id);
    tr.setAttribute("data-selected", String(sel));
    const cb = tr.querySelector<HTMLInputElement>('input[type="checkbox"][data-card-row-cb]');
    if (cb) cb.checked = sel;
  });
  // Single-item actions: only fire when exactly one lot is selected.
  ["cardsActRefresh", "cardsActLookup", "cardsActEbay"].forEach(id => {
    const btn = document.getElementById(id) as HTMLButtonElement | null;
    if (btn) btn.disabled = size !== 1;
  });
  // Bulk actions: fire when at least one lot is selected.
  ["cardsActDelete", "cardsActTrade", "cardsActSale", "cardsActMarkListed", "cardsActMarkAvail", "cardsActLabels"].forEach(id => {
    const btn = document.getElementById(id) as HTMLButtonElement | null;
    if (btn) btn.disabled = size < 1;
  });
  const lbl = document.getElementById("cardsSelLabel");
  if (lbl) {
    if (size === 0)      lbl.textContent = "";
    else if (size === 1) lbl.textContent = Array.from(_selectedCardLots)[0]!;
    else                 lbl.textContent = `${size} selected`;
  }
  const bar = document.getElementById("cardsSelectionBar");
  if (bar) bar.style.display = size === 0 ? "none" : "flex";
  // Header "select all" checkbox: checked if every visible row is selected.
  const sa = document.getElementById("cardsSelectAllCb") as HTMLInputElement | null;
  if (sa) {
    const rows = document.querySelectorAll<HTMLElement>("tr[data-cards-row]");
    const all = rows.length > 0 && Array.from(rows).every(r => _selectedCardLots.has(r.getAttribute("data-cards-row") || ""));
    sa.checked = all;
    sa.indeterminate = !all && size > 0;
  }
}

/** Convenience: pull the single selected lot ID, or null if size !== 1. */
function _singleSelected(): string | null {
  return _selectedCardLots.size === 1 ? Array.from(_selectedCardLots)[0]! : null;
}

/** Action dispatchers - each guards against the wrong selection size. */
function cardsToolbarEdit():    void { const id = _singleSelected(); if (id) editCard(id); }
function cardsToolbarLabels():  void { openLabelPrint("card", Array.from(_selectedCardLots)); }
function cardsToolbarRefresh(): void {
  const id = _singleSelected(); if (!id) return;
  const fn = (window as any).refreshCardFMV;
  if (typeof fn === "function") fn(id);
}
function cardsToolbarLookup():  void { const id = _singleSelected(); if (id) lookupCardPrice(id); }
function cardsToolbarEbay():    void { const id = _singleSelected(); if (id) openEbaySoldsFor("card", id); }
function cardsToolbarDelete():  void {
  if (_selectedCardLots.size === 0) return;
  const ids = Array.from(_selectedCardLots);
  if (ids.length > 1 && !confirm(`Delete ${ids.length} card lots? This can't be undone.`)) return;
  const fn = (window as any).deleteRow;
  if (typeof fn !== "function") return;
  for (const id of ids) fn("cards", "Card Lot ID", id);
  _selectedCardLots.clear();
  applyCardSelectionUI();
}
function cardsToolbarTrade(): void {
  if (_selectedCardLots.size === 0) return;
  const fn = (window as any).generateTradeFromLots;
  if (typeof fn === "function") fn(Array.from(_selectedCardLots), "card");
}
function cardsToolbarSale():  void {
  if (_selectedCardLots.size === 0) return;
  const fn = (window as any).generateSaleFromLots;
  if (typeof fn === "function") fn(Array.from(_selectedCardLots), "card");
}
function cardsToolbarMark(value: string): void {
  if (_selectedCardLots.size === 0) return;
  bulkSetStatus("cards", Array.from(_selectedCardLots), value);
}
(window as any).cardsToolbarMark    = cardsToolbarMark;
(window as any).cardsToolbarEdit    = cardsToolbarEdit;
(window as any).cardsToolbarRefresh = cardsToolbarRefresh;
(window as any).cardsToolbarLookup  = cardsToolbarLookup;
(window as any).cardsToolbarEbay    = cardsToolbarEbay;
(window as any).cardsToolbarDelete  = cardsToolbarDelete;
(window as any).cardsToolbarTrade   = cardsToolbarTrade;
(window as any).cardsToolbarSale    = cardsToolbarSale;

/** Opens the Export Options modal, then re-applies the viewCards() filter chain
 *  and hands the chosen format/detail off to the report renderer.
 */
export function exportCardsReportFromView(): void {
  openExportOptions("Cards Inventory", (format, detail) => {
    const f = CardsFilters;
    let rows = DB.table("cards").slice().filter(r => !isBulkPoolRow(r));
    if (f.search) {
      const q = f.search.toLowerCase();
      rows = rows.filter(r => Object.values(r).some(v => String(v || "").toLowerCase().includes(q)));
    }
    if (f.game)           rows = rows.filter(r => (r["Game"] || "") === f.game);
    if (f.status)         rows = rows.filter(r => r["Status"] === f.status);
    if (f.inventoryClass) rows = rows.filter(r => r["Inventory Class"] === f.inventoryClass);
    if (!f.showZeroQty)   rows = rows.filter(r => (Number(r["Qty"]) || 0) > 0);
    exportCardsReport(format, detail, rows, f);
  });
}
(window as any).exportCardsReportFromView = exportCardsReportFromView;

/** Download the currently-filtered (and sorted) card lots as a raw-value CSV. */
export function exportCardsCsv(): void {
  const f = CardsFilters;
  let rows = DB.table("cards").slice().filter(r => !isBulkPoolRow(r));
  if (f.search) {
    const q = f.search.toLowerCase();
    rows = rows.filter(r => Object.values(r).some(v => String(v || "").toLowerCase().includes(q)));
  }
  if (f.game)           rows = rows.filter(r => (r["Game"] || "") === f.game);
  if (f.status)         rows = rows.filter(r => r["Status"] === f.status);
  if (f.inventoryClass) rows = rows.filter(r => r["Inventory Class"] === f.inventoryClass);
  if (!f.showZeroQty)   rows = rows.filter(r => (Number(r["Qty"]) || 0) > 0);
  const sort = sectionSort("cards");
  if (sort && CARD_SORT_ACCESSORS[sort.key]) rows = sortRowsForView(rows, sort.dir, CARD_SORT_ACCESSORS[sort.key]!);
  const unit = (r: Row) => { const q = Number(r["Qty"]) || 0; const t = Number(r["Current FMV"]) || 0; return q > 0 ? +(t / q).toFixed(2) : t; };
  downloadCsv(`cards-inventory-${todayISO()}.csv`, rows, [
    { label: "Lot ID",          get: r => r["Card Lot ID"] },
    { label: "Game",            get: r => r["Game"] },
    { label: "Card Name",       get: r => r["Card Name"] },
    { label: "Set",             get: r => r["Set"] },
    { label: "Card #",          get: r => r["Card #"] },
    { label: "Condition/Grade", get: r => r["Condition/Grade"] },
    { label: "Class",           get: r => r["Inventory Class"] },
    { label: "Status",          get: r => r["Status"] },
    { label: "Qty",             get: r => r["Qty"] },
    { label: "Unit Basis",      get: r => r["Unit Basis"] },
    { label: "Total Basis",     get: r => r["Total Basis"] },
    { label: "Unit FMV",        get: unit },
    { label: "Total FMV",       get: r => r["Current FMV"] },
    { label: "G/L",             get: r => +(((Number(r["Current FMV"]) || 0) - (Number(r["Total Basis"]) || 0))).toFixed(2) },
    { label: "Acq Date",        get: r => fmtDate(r["Acq Date"]) },
    { label: "Acq Type",        get: r => r["Acq Type"] },
    { label: "Source ID",       get: r => r["Source ID"] },
    { label: "Notes",           get: r => r["Notes"] },
  ]);
  toast(`CSV downloaded \u00b7 ${rows.length} row(s)`);
}
(window as any).exportCardsCsv = exportCardsCsv;

/** Shared Status list + the row's current value appended when it's a legacy
 *  value no longer in the list — so opening the edit form never silently
 *  blanks the field. (Same pattern as cardInventoryClassOptions below.) */
function statusOptionsFor(currentValue: string | undefined): string[] {
  const list = Lists.get("Status");
  if (currentValue && !list.includes(currentValue)) return [...list, currentValue];
  return list;
}

/** The canonical Inventory Class options for card lots. "Flip" and
 *  "Long Term Hold" were retired in favor of the three Bulk tiers. */
const CARD_INVENTORY_CLASSES = ["Raw", "Slab", "Bulk", "Holo Bulk", "Premium Bulk", "$1-$5 Bulk", "$5-$10 Bulk", "Other"];

/** Form options for a card's Inventory Class. If the row already has a value
 *  not in the canonical list (e.g. a legacy "Flip" / "Long Term Hold" entry),
 *  include it as a trailing option so opening the edit form doesn't silently
 *  blank the field. New cards can't pick the legacy values from the dropdown. */
function cardInventoryClassOptions(currentValue: string | undefined): string[] {
  if (currentValue && !CARD_INVENTORY_CLASSES.includes(currentValue)) {
    return [...CARD_INVENTORY_CLASSES, currentValue];
  }
  return CARD_INVENTORY_CLASSES;
}

export function viewCards(root: HTMLElement): void {
  const f = CardsFilters;
  // Bulk pool singletons live in the cards table but display separately above.
  let rows = DB.table("cards").slice().filter(r => !isBulkPoolRow(r));
  if (f.search) {
    const q = f.search.toLowerCase();
    rows = rows.filter(r => Object.values(r).some(v => String(v || "").toLowerCase().includes(q)));
  }
  if (f.game)           rows = rows.filter(r => (r["Game"] || "") === f.game);
  if (f.status)         rows = rows.filter(r => r["Status"] === f.status);
  if (f.inventoryClass) rows = rows.filter(r => r["Inventory Class"] === f.inventoryClass);
  // Hide lots with no stock left unless the user opts in via the toggle.
  // Empty lots stay in the database (we still need their history) but they
  // clutter the active-inventory view.
  if (!f.showZeroQty)   rows = rows.filter(r => (Number(r["Qty"]) || 0) > 0);

  // Global sort: order the FULL filtered set before pagination (the header
  // click stores the sort; the old DOM sort only ordered the visible page).
  const sort = sectionSort("cards");
  if (sort && CARD_SORT_ACCESSORS[sort.key]) rows = sortRowsForView(rows, sort.dir, CARD_SORT_ACCESSORS[sort.key]!);

  const totalQty   = sum(rows, r => r["Qty"]);
  const totalBasis = sum(rows, r => r["Total Basis"]);
  const totalFMV   = sum(rows, r => r["Current FMV"]);
  const unrlz      = totalFMV - totalBasis;

  const rows_total = rows.length;
  const rows_page = slicePage("cards", rows, 100);
  root.innerHTML = `
    ${kpiBar([
      { label: "Lots shown", value: fmtN(rows.length) },
      { label: "Cards",      value: fmtN(totalQty) },
      { label: "Cost Basis", value: fmt$0(totalBasis) },
      { label: "FMV",        value: fmt$0(totalFMV), tone: unrlz >= 0 ? "green" : "red" },
      { label: "Unrealized", value: (unrlz >= 0 ? "+" : "\u2212") + fmt$0(Math.abs(unrlz)), tone: unrlz >= 0 ? "green" : "red" },
    ])}

    ${renderBulkPoolsPanel()}

    <div class="panel p-3 mb-3 flex flex-col gap-2">
      <!-- Row 1: search + filter dropdowns + clear + export -->
      <div class="flex flex-wrap gap-2 items-center">
        <div class="relative flex-1 min-w-[180px]">
          <input class="input w-full pr-8"
                 placeholder="Search name, set, lot ID, notes…"
                 value="${escapeHtml(f.search)}"
                 oninput="cardsSearchInput(this.value)"
                 data-focus-key="cards-search">
          ${f.search ? `<button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white text-sm leading-none" onmousedown="event.preventDefault()" onclick="CardsFilters.search='';render()" title="Clear search">✕</button>` : ""}
        </div>
        <select class="select !w-auto" title="Filter by game" onchange="CardsFilters.game=this.value;render()">
          <option value="">All games</option>
          ${GAMES.map(o => `<option ${f.game === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
        </select>
        <select class="select !w-auto" title="Filter by status" onchange="CardsFilters.status=this.value;render()">
          <option value="">All statuses</option>
          ${Lists.get("Status").map(o => `<option ${f.status === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
        </select>
        <select class="select !w-auto" title="Filter by inventory class" onchange="CardsFilters.inventoryClass=this.value;render()">
          <option value="">All classes</option>
          ${CARD_INVENTORY_CLASSES.map(o => `<option ${f.inventoryClass === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
        </select>
        <label class="text-xs flex items-center gap-1 cursor-pointer select-none whitespace-nowrap" title="Show lots that have been fully sold, traded, ripped, or gifted (Qty = 0)">
          <input type="checkbox" onchange="CardsFilters.showZeroQty=this.checked;render()" ${f.showZeroQty ? "checked" : ""}>
          Show empty
        </label>
        ${(f.search || f.game || f.status || f.inventoryClass || f.showZeroQty)
          ? `<button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="CardsFilters.search='';CardsFilters.game='';CardsFilters.status='';CardsFilters.inventoryClass='';CardsFilters.showZeroQty=false;render()">Clear</button>`
          : ""}
        <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="openBulkScan()" title="Scan a stack of cards with the camera - exact matches save themselves with price and catalog link">${icon("camera")} Bulk Scan</button>
        <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportCardsReportFromView()" title="Export currently-visible rows as a printable PDF report">${icon("printer")} Export</button>
        <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportCardsCsv()" title="Download the currently-filtered rows as a CSV (raw values, Excel/Sheets-ready)">${icon("download")} CSV</button>
        <div data-cols-host></div>
      </div>
      <!-- Selection bar — appears only when rows are checked. Refresh/Lookup
           need exactly one checked row; the rest work on any number. -->
      <div id="cardsSelectionBar" class="flex flex-wrap gap-2 items-center pt-2 border-t border-[var(--border)]" style="display:none">
        <span class="text-xs text-[var(--muted)]">Selected: <code id="cardsSelLabel"></code></span>
        <div class="ml-auto flex gap-1 flex-wrap">
          <button id="cardsActRefresh" class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarRefresh()" title="Refresh FMV from market (one row only)">${icon("refresh")} Refresh FMV</button>
          <button id="cardsActLookup"  class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarLookup()"  title="Search this card on TCGplayer (one row only)">${icon("search")} Lookup</button>
          <button id="cardsActEbay"    class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarEbay()"    title="Open eBay SOLD listings for this card, newest first — real recent sale prices (one row only)">${icon("dollar-sign")} eBay Solds</button>
          <button id="cardsActMarkListed" class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarMark('Listed')" title="Mark every selected Available/Listed lot as Listed (machine-managed statuses are skipped)">${icon("tag")} Mark Listed</button>
          <button id="cardsActMarkAvail"  class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarMark('Available')" title="Mark every selected Available/Listed lot as Available">${icon("check-circle")} Mark Available</button>
          <button id="cardsActLabels"  class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarLabels()" title="Print QR labels for the selected lots (label-writer friendly)">${icon("tag")} Labels</button>
          <button id="cardsActTrade"   class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarTrade()"   title="Open a new trade with the selected lots as outgoing items">${icon("route")} Generate Trade</button>
          <button id="cardsActSale"    class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="cardsToolbarSale()"    title="Open a new sale with the selected lots as items">${icon("wallet")} Generate Sale</button>
          <button id="cardsActDelete"  class="btn btn-ghost !py-1 !px-2 text-xs !text-rose-300" disabled onclick="cardsToolbarDelete()" title="Delete selected card lots (any number)">${icon("trash")} Delete</button>
        </div>
      </div>
    </div>

    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th class="!w-8"><input type="checkbox" id="cardsSelectAllCb" onclick="cardsSelectAll(this.checked)" title="Select / deselect all visible rows"></th><th></th><th data-col="Lot ID">Lot ID</th><th data-col="Game">Game</th><th data-col="Card" class="cell-clip">Card</th><th data-col="Set" class="cell-clip">Set</th><th data-col="#">#</th><th data-col="Cond/Grade">Cond/Grade</th><th data-col="Class">Class</th><th data-col="Status">Status</th>
          <th data-col="Qty" class="text-right">Qty</th><th data-col="Unit Basis" class="text-right">Unit Basis</th><th data-col="Total Basis" class="text-right">Total Basis</th>
          <th data-col="Unit FMV" class="text-right" title="Market value per single card — what you'd ask for one unit">Unit FMV</th><th data-col="Total FMV" class="text-right">Total FMV</th><th data-col="G/L" class="text-right">G/L</th><th data-col="Acq Date">Acq Date</th>
          <th data-col="Source" class="cell-clip">Source</th>
        </tr></thead>
        <tbody>
          ${rows_page.map(r => {
            const totalFMVrow = Number(r["Current FMV"]) || 0;
            const qty         = Number(r["Qty"]) || 0;
            const unitFMV     = qty > 0 ? totalFMVrow / qty : totalFMVrow;
            const gl          = totalFMVrow - (Number(r["Total Basis"]) || 0);
            const lotId       = escapeHtml(r["Card Lot ID"] || "");
            const srcId       = r["Source ID"] ? escapeHtml(r["Source ID"]) : "";
            const imgUrl = r["Image URL"] || "";
            const isSel = _selectedCardLots.has(lotId);
            return `<tr data-cards-row="${lotId}" data-selectable data-selected="${isSel}"
                        onclick="cardRowClick(event, '${lotId}')">
              <td class="text-center !w-8"><input type="checkbox" data-card-row-cb ${isSel ? "checked" : ""} onclick="cardRowToggle('${lotId}', this.checked); event.stopPropagation()"></td>
              <td class="text-center">${imgUrl ? `<img src="${escapeHtml(imgUrl)}" loading="lazy" class="inline-block rounded thumb-hover" style="height:40px; width:auto; max-width:30px" alt="">` : ""}</td>
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${lotId}')">${lotId}</a></td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(r["Game"] || "—")}</td>
              <td class="cell-clip">${escapeHtml(r["Card Name"] || "")}</td>
              <td class="cell-clip">${escapeHtml(r["Set"] || "")}</td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(r["Card #"] || "")}</td>
              <td>${escapeHtml(r["Condition/Grade"] || "")}</td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(r["Inventory Class"] || "")}</td>
              <td>${statusCellHtml("cards", String(r["Card Lot ID"] || ""), String(r["Status"] || "Available"))}</td>
              <td class="text-right stat-num">${fmtN(r["Qty"])}</td>
              <td class="text-right stat-num">${fmt$(r["Unit Basis"])}</td>
              <td class="text-right stat-num">${fmt$(r["Total Basis"])}</td>
              <td class="text-right stat-num">${r["FMV Locked"] ? `<span title="FMV locked (manual) \u2014 auto-refresh skips this lot">${icon("lock", { size: 12 })}</span> ` : ""}${fmt$(unitFMV)}</td>
              <td class="text-right stat-num">${fmt$(totalFMVrow)}</td>
              <td class="text-right stat-num ${gl >= 0 ? "text-emerald-300" : "text-rose-300"}">${fmt$(gl)}</td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Acq Date"]))}</td>
              <td class="font-mono text-xs text-[var(--muted)] cell-clip">${srcId ? `<a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${srcId}')">${srcId}</a>` : ""}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="18" class="text-center text-[var(--muted)] py-10">
              <div class="mb-3">No cards match. Add your first card, or scan a stack with the camera.</div>
              <button class="btn btn-primary !py-1.5 !px-3 text-sm" onclick="editCard()">${icon("plus")} Add Card</button>
              <button class="btn btn-ghost !py-1.5 !px-3 text-sm ml-2" onclick="openBulkScan()">${icon("camera")} Bulk Scan</button>
            </td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("cards", rows_total, 100)}
  `;
  // Apply selection highlight + toolbar enabled state after innerHTML lands.
  applyCardSelectionUI();
}

// re-expose for inline handlers (already exposed in state.ts but safe to set again)
(window as any).render = render;

/* ============================================================
   Card lot form (editCard / editCardWithRow / saveCard)
   ============================================================ */
export function editCard(id?: string): void {
  const isNew = !id;
  const row = isNew ? {
    "Card Lot ID": nextId("C", "cards", "Card Lot ID"),
    "Acq Date": todayISO(),
    "Acq Type": "Purchase",
    "Inventory Class": "Raw",
    "Status": "Available",
    "Game": "Pokemon",
    "Qty": 1,
    "Source Lot/Rip ID": "N/A",
  } : DB.table("cards").find(r => r["Card Lot ID"] === id);
  if (!row) { toast("Card not found", "err"); return; }
  editCardWithRow(row as Row, isNew);
}

export function editCardWithRow(row: Row, isNew: boolean): void {
  // Source ID dropdown union: purchases + adjustments + rips + trades
  const sourceOpts = [
    ...DB.table("purchases").map(p => p["Purchase ID"]).filter(Boolean),
    ...DB.table("transactions").map(t => t["Txn ID"]).filter(Boolean),
    ...DB.table("rips").map(r => r["Rip ID"]).filter(Boolean),
    ...DB.table("trades").filter(t => String(t["Status"] || "Accepted") === "Accepted").map(t => t["Trade ID"]).filter(Boolean),
  ];

  const body = `
    <form id="cardForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
      ${formField({ label: "Card Lot ID", name: "Card Lot ID", value: row["Card Lot ID"], required: true })}
      ${formField({ label: "Acq Date", name: "Acq Date", type: "date", value: fmtDate(row["Acq Date"]), max: todayISO() })}
      <div class="sm:col-span-2 flex gap-2 items-end">
        <div class="flex-1">${formField({ label: "Card Name", name: "Card Name", value: row["Card Name"], required: true, hint: "Tip: type name + number (<code>Pikachu TG05</code>, <code>Charizard 4</code>) then click ⌕ Lookup to find the card across all sets. Numbers alone (<code>176</code>, <code>TG05</code>) or full API IDs (<code>sv1-176</code>) also work." })}</div>
        <button type="button" class="btn btn-ghost" onclick="openCardSightScan()" title="Scan a photo of the card to identify it automatically (CardSight)">${icon("camera")} Scan</button>
        <button type="button" class="btn btn-ghost" onclick="quickCardLookup()" title="Search the card catalog. Type name + number first (e.g. 'Pikachu TG05'), or just the number/ID.">${icon("search")} Lookup</button>
      </div>
      ${formField({ label: "Game", name: "Game", value: row["Game"] || "Pokemon", options: GAMES, required: true })}
      ${comboboxField({ label: "Set", name: "Set", value: row["Set"] || "", placeholder: "Type to search sets…", hint: "Auto-loads sets for the selected game (cached locally)." })}
      ${formField({ label: "Card #", name: "Card #", value: row["Card #"] })}
      ${formField({ label: "Inventory Class", name: "Inventory Class", value: row["Inventory Class"], options: cardInventoryClassOptions(row["Inventory Class"]) })}
      ${gradePickerHtml(row["Condition/Grade"])}
      ${formField({ label: "Acq Type", name: "Acq Type", value: row["Acq Type"], options: Lists.get("Acq Type") })}
      ${formField({ label: "Status", name: "Status", value: row["Status"], options: statusOptionsFor(row["Status"]) })}
      ${formField({ label: "Qty", name: "Qty", type: "number", step: "1", value: row["Qty"] })}
      <div>
        <label class="label" for="f_Unit_Basis">Unit Basis ($)</label>
        <div class="flex gap-2">
          <input class="input flex-1" id="f_Unit_Basis" name="Unit Basis" type="number" step="0.01" value="${row["Unit Basis"] ?? ""}" ${basisProvenance(row["Source ID"]) ? "readonly" : ""}>
          ${(() => { const bp = basisProvenance(row["Source ID"]); return bp && bp.overridable ? `<button type="button" class="btn btn-ghost whitespace-nowrap" id="basisOverrideBtn" title="Unlock the basis for a deliberate manual correction">${icon("edit")} Override</button>` : ""; })()}
        </div>
        ${(() => {
          const bp = basisProvenance(row["Source ID"]);
          const src = escapeHtml(String(row["Source ID"] || ""));
          if (!bp) return `<div class="text-xs text-[var(--muted)] mt-1">Cost per single card</div>`;
          return `<div class="text-xs text-[var(--muted)] mt-1">Calculated by ${bp.kind} <a class="font-mono text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${src}')">${src}</a> \u2014 ${bp.how}.${bp.overridable ? " Prefer editing the source transaction; Override is for deliberate corrections only." : ""}</div>`;
        })()}
      </div>
      <div>
        <label class="label" for="f_Current_FMV">Unit FMV ($)</label>
        <div class="flex gap-2">
          <input class="input flex-1" id="f_Current_FMV" name="Current FMV" type="number" step="0.01" value="${unitFMVForForm(row)}">
          <button type="button" class="btn btn-ghost whitespace-nowrap" onclick="refreshFMVInForm('card')" title="Look up market FMV per unit">${icon("refresh")} Lookup</button>
        </div>
        <label class="flex items-center gap-2 text-xs mt-2 cursor-pointer select-none" title="When locked, the daily FMV auto-sweep and Refresh-all never overwrite this lot's FMV. Manual price entry locks automatically.">
          <input type="checkbox" name="FMV Locked" ${row["FMV Locked"] ? "checked" : ""}> ${icon("lock", { size: 13 })} Lock FMV (protect from auto-refresh)
        </label>
        <div class="text-xs text-[var(--muted)] mt-1">Total Basis and Total FMV are computed automatically (× Qty) and shown on the main table.</div>
      </div>
      ${variantSelectHtml(row["Price Variant"])}
      <div>
        <label class="label">Price Source</label>
        <div class="text-sm py-2" id="cardPriceSource"></div>
      </div>
      ${formField({ label: "Source ID", name: "Source ID", value: row["Source ID"], options: sourceOpts, hint: "Link to a purchase (PUR-…), adjustment (TXN-…), rip (R-…), or trade (TR-…)" })}
      <div class="sm:col-span-2">
        <label class="label" for="f_Image_URL">Image URL</label>
        <div class="flex gap-2 items-start">
          <input class="input flex-1" id="f_Image_URL" name="Image URL" value="${escapeHtml(row["Image URL"] || "")}" placeholder="Auto-fills from ⌕ Lookup; or paste any thumbnail URL" oninput="window._cardThumbPreview && window._cardThumbPreview(this.value)">
          <img id="cardThumbPreview" src="${escapeHtml(row["Image URL"] || "")}" class="${row["Image URL"] ? "" : "hidden"} rounded border border-[var(--border)] shrink-0" style="height:80px; width:auto; max-width:60px" alt="">
        </div>
        <div class="text-xs text-[var(--muted)] mt-1">Filled automatically by ⌕ Lookup. Leave blank for no thumbnail.</div>
      </div>
      <div class="sm:col-span-2">${formField({ label: "Notes", name: "Notes", type: "textarea", value: row["Notes"] })}</div>
      <!-- Canonical catalog identity (Phase 1c) — filled by ⌕ Lookup / Scan picks,
           round-tripped invisibly so edits don't wipe them. -->
      <input type="hidden" name="TCG Product ID" value="${escapeHtml(row["TCG Product ID"] ?? "")}">
      <input type="hidden" name="PokemonTCG ID" value="${escapeHtml(row["PokemonTCG ID"] ?? "")}">
      <input type="hidden" name="Scryfall ID" value="${escapeHtml(row["Scryfall ID"] ?? "")}">
      <!-- "Price Variant" is a VISIBLE select (Printing / Edition) above — it is
           listed here only as a reminder that it round-trips like the IDs do. -->
    </form>`;
  const safeId = escapeHtml(row["Card Lot ID"]);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteRow('cards','Card Lot ID','${safeId}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    ${isNew ? `<button class="btn btn-ghost" onclick="saveCardAndAddAnother()" title="Save this card and immediately start another with the same Set / Condition / Acq fields prefilled">Save & Add Another</button>` : ""}
    <button class="btn btn-primary" onclick="saveCard('${isNew ? "" : safeId}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Card Lot" : "Edit Card Lot", body, footer, wide: true });

  // Live thumbnail preview when the user pastes a URL or ⌕ Lookup fills it.
  (window as any)._cardThumbPreview = (url: string) => {
    const img = document.getElementById("cardThumbPreview") as HTMLImageElement | null;
    if (!img) return;
    if (url && url.trim()) {
      img.src = url.trim();
      img.classList.remove("hidden");
    } else {
      img.removeAttribute("src");
      img.classList.add("hidden");
    }
  };

  // Derived price-source indicator — replaces the old Valuation Source
  // dropdown. Computed from catalog link + lock state; updates on lock toggle.
  const updateCardPriceSource = () => {
    const el = document.getElementById("cardPriceSource");
    if (!el) return;
    const q = (sel: string) => (document.querySelector<HTMLInputElement>(sel)?.value || "").trim();
    const locked = !!document.querySelector<HTMLInputElement>('#cardForm [name="FMV Locked"]')?.checked;
    const linked = !!(q('#cardForm [name="TCG Product ID"]') || q('#cardForm [name="PokemonTCG ID"]') || q('#cardForm [name="Scryfall ID"]'));
    el.innerHTML = locked ? `${icon("lock", { size: 13 })} Manual \u2014 locked; auto-repricing skips this lot`
      : linked ? `${icon("link", { size: 13 })} Catalog-linked \u2014 repriced nightly`
      : `${icon("edit", { size: 13 })} Unlinked \u2014 manual price; use ${icon("search", { size: 13 })} Lookup to enable auto-repricing`;
  };
  updateCardPriceSource();
  document.querySelector<HTMLInputElement>('#cardForm [name="FMV Locked"]')?.addEventListener("change", updateCardPriceSource);

  // Derived-basis soft lock: Override unlocks after an explicit consequence
  // confirm. (Rip pulls have no Override — reallocation recomputes them.)
  document.getElementById("basisOverrideBtn")?.addEventListener("click", () => {
    const src = String(row["Source ID"] || "");
    if (!confirm(`This basis was calculated from ${src}. Overriding disconnects this lot from its source transaction's totals \u2014 Books Health will flag the difference, and future sales will use YOUR number for COGS.\n\nOverride the basis?`)) return;
    const inp = document.getElementById("f_Unit_Basis") as HTMLInputElement | null;
    if (inp) { inp.readOnly = false; inp.focus(); inp.select(); }
    document.getElementById("basisOverrideBtn")?.remove();
  });

  const form = $("#cardForm") as HTMLFormElement;
  wireGradePicker(form);
  // Printing / edition selector: populate from the catalog for the linked
  // product (no-op when the lot is unlinked) and keep the FMV field in step
  // with whichever tier is chosen.
  void syncVariantSelect(form, Number(row["TCG Product ID"]) || 0, row["Price Variant"]);
  wireVariantSelect(form, "Current FMV");
  const gameSel = form.querySelector<HTMLSelectElement>('[name="Game"]')!;
  const setWrapper = form.querySelector<HTMLElement>('.combobox-wrapper[data-cbx="Set"]')!;
  let currentGame = gameSel.value || "Pokemon";

  const cbx = wireCombobox(setWrapper, {
    getOptions: () => SetCache.get(currentGame),
    asyncLoader: () => SetCache.ensureFresh(currentGame).then(() => cbx.refresh()),
  });

  gameSel.addEventListener("change", () => {
    currentGame = gameSel.value || "Pokemon";
    setWrapper.querySelector<HTMLInputElement>("input")!.value = "";
    cbx.refresh();
    SetCache.ensureFresh(currentGame).then(() => cbx.refresh());
  });
}

export function saveCard(origId: string): void {
  const data = readForm($("#cardForm"));
  const qty       = Number(data["Qty"])         || 0;
  const unitBasis = Number(data["Unit Basis"])  || 0;
  const unitFMV   = Number(data["Current FMV"]) || 0;
  // Canonical IDs: hidden text inputs -> typed columns (bigint / text / uuid).
  data["TCG Product ID"] = Number(data["TCG Product ID"]) > 0 ? Number(data["TCG Product ID"]) : null;
  data["PokemonTCG ID"]  = String(data["PokemonTCG ID"] || "").trim() || null;
  data["Scryfall ID"]    = String(data["Scryfall ID"] || "").trim() || null;
  // "" = Auto (follow the default ladder); null keeps that meaning in Postgres.
  data["Price Variant"]  = String(data["Price Variant"] || "").trim() || null;
  data["Set"]            = normalizeSetName(data["Set"]);
  // A graded Condition/Grade means the card IS a slab — enforce the class so
  // a manually-typed grade can't leave a PSA 10 marked Raw (2026-07-26).
  if (looksGraded(String(data["Condition/Grade"] || "")) && data["Inventory Class"] !== "Slab") {
    data["Inventory Class"] = "Slab";
    toast("Graded condition detected \u2014 Inventory Class set to Slab");
  }
  data["Total Basis"]    = +(unitBasis * qty).toFixed(2);
  data["Current FMV"]    = +(unitFMV   * qty).toFixed(2);
  data["Unrealized G/L"] = +(data["Current FMV"] - data["Total Basis"]).toFixed(2);

  // Detect whether the card moved between rip / non-rip sources so we can
  // reallocate basis on whichever rip(s) are affected. The previous Source ID
  // is captured BEFORE the save so we can compare.
  const newSourceId = String(data["Source ID"] || "");
  let prevSourceId = "";
  if (origId) {
    const prev = DB.table("cards").find(r => r["Card Lot ID"] === origId);
    prevSourceId = String(prev?.["Source ID"] || "");
  }

  let savedRow: Record<string, any> | null = data;
  if (origId) savedRow = DB.update("cards", "Card Lot ID", origId, data);
  else        void DB.add("cards", data);   // DB.add stores `data` by reference; _uuid lands on it async

  // Phase 4: if the thumbnail points at an external catalog CDN, ask the
  // server to copy it into our own storage (fire-and-forget).
  queueImageCache("cards", savedRow || data);

  // Recompute rip basis if this card is currently attached to a rip, or was
  // previously attached to a (different) rip. Order matters: do the *new*
  // rip last so its allocation reflects the latest data.
  if (prevSourceId && prevSourceId !== newSourceId && sourceIdIsRip(prevSourceId)) {
    reallocateRipBasis(prevSourceId);
  }
  if (newSourceId && sourceIdIsRip(newSourceId)) {
    reallocateRipBasis(newSourceId);
  }
  // If Source ID points at an existing purchase or adjustment, ensure that parent
  // lists this lot in its Items[] (idempotent — safe to call on every save).
  syncLotToSource(data["Card Lot ID"], "Card", data);
  toast("Card saved");
  closeModal();
  render();
}

/** ⌕ button on a card row — opens TCGplayer search for that card in a new tab. */
export function lookupCardPrice(id: string): void {
  const c = DB.table("cards").find(r => r["Card Lot ID"] === id);
  if (!c) return;
  const q = [c["Card Name"] || "", c["Set"] || ""].filter(Boolean).join(" ").trim();
  if (!q) { toast("No card info to search.", "err"); return; }
  window.open("https://www.tcgplayer.com/search/all/product?q=" + encodeURIComponent(q), "_blank", "noopener");
}

/* ============================================================
   Bulk Pools panel (sits above the regular cards table)
   ============================================================ */
function poolPillColor(klass: BulkClass): string {
  if (klass === "Bulk")       return "gray";
  if (klass === "Holo Bulk")  return "violet";
  return "amber"; // Premium Bulk
}

/** localStorage key — remembers whether the bulk-pool panel is expanded. */
const BULK_PANEL_KEY = "enigma_bulk_panel_open_v1";
function bulkPanelIsOpen(): boolean {
  try { return localStorage.getItem(BULK_PANEL_KEY) !== "0"; } catch { return true; }
}
export function toggleBulkPanel(el: HTMLDetailsElement): void {
  try { localStorage.setItem(BULK_PANEL_KEY, el.open ? "1" : "0"); } catch { /* ignore */ }
}
(window as any).toggleBulkPanel = toggleBulkPanel;

function renderBulkPoolsPanel(): string {
  // Per-class tile (3 of these inside the expanded panel)
  const tiles = BULK_CLASSES.map(klass => {
    const pool = getBulkPool(klass);
    const qty = pool ? Number(pool["Qty"]) || 0 : 0;
    const unitBasis = bulkUnitValueFor(klass);
    const totalBasis = +(qty * unitBasis).toFixed(2);
    const poolId = bulkPoolLotId(klass);
    // Tile body is clickable → opens Lot History for the pool. The edit button
    // calls stopPropagation so it doesn't also fire the history modal.
    return `
      <div class="panel-2 px-3 py-1.5 flex items-center gap-2 text-sm cursor-pointer hover:bg-[var(--panel-2-hover,rgba(255,255,255,0.04))]"
           onclick="showLotHistory('${escapeHtml(poolId)}')" title="Click for full activity history">
        <span class="pill pill-${poolPillColor(klass)} shrink-0">${escapeHtml(klass)}</span>
        <span class="stat-num font-semibold">${fmtN(qty)}</span>
        <span class="text-xs text-[var(--muted)] truncate">@ ${fmt$(unitBasis)}/c</span>
        <span class="ml-auto stat-num"><b>${fmt$(totalBasis)}</b></span>
        <button class="btn btn-ghost !py-0.5 !px-2 text-xs shrink-0"
                onclick="event.stopPropagation(); adjustBulkPool('${escapeHtml(klass)}')"
                title="Adjust quantity">${icon("edit")}</button>
      </div>`;
  }).join("");

  // Roll-up totals shown in the collapsed summary line
  let totalQty = 0, totalBasis = 0;
  BULK_CLASSES.forEach(klass => {
    const pool = getBulkPool(klass);
    const q = pool ? Number(pool["Qty"]) || 0 : 0;
    totalQty += q;
    totalBasis += q * bulkUnitValueFor(klass);
  });
  totalBasis = +totalBasis.toFixed(2);

  const open = bulkPanelIsOpen();
  return `
    <details class="mb-3" ${open ? "open" : ""} ontoggle="toggleBulkPanel(this)">
      <summary class="panel-2 px-3 py-1.5 flex items-center gap-2 text-sm cursor-pointer hover:bg-[var(--panel-2-hover,rgba(255,255,255,0.04))] select-none">
        <span class="chev shrink-0">${icon("chevron-down", { size: 14 })}</span>
        <span class="text-xs text-[var(--muted)] uppercase tracking-wider">Bulk pools</span>
        <span class="stat-num font-semibold ml-2">${fmtN(totalQty)}</span>
        <span class="text-xs text-[var(--muted)]">cards across 3 pools</span>
        <span class="ml-auto stat-num"><b>${fmt$(totalBasis)}</b></span>
      </summary>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">${tiles}</div>
    </details>`;
}

/** Manual qty-adjust modal — accepts a signed integer (e.g. -50 or 200). */
export function adjustBulkPool(klassRaw: string): void {
  const klass = klassRaw as BulkClass;
  if (!BULK_CLASSES.includes(klass)) { toast("Unknown bulk class", "err"); return; }
  const pool = getBulkPool(klass);
  const cur = pool ? Number(pool["Qty"]) || 0 : 0;
  const body = `
    <div class="space-y-3">
      <div class="text-sm">Current pool: <b>${fmtN(cur)}</b> cards in <span class="pill pill-${poolPillColor(klass)}">${escapeHtml(klass)}</span></div>
      <div>
        <label class="label">Quantity change (signed — e.g. <code>+50</code> adds, <code>-10</code> removes)</label>
        <input class="input" id="bulkAdjustDelta" type="number" step="1" placeholder="e.g. 50 or -10" autofocus>
      </div>
      <div class="text-xs text-[var(--muted)]">Pool basis recomputes automatically from the current Settings per-card value. The pool's Qty floors at 0 — you can't go negative.</div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="commitBulkPoolAdjust('${escapeHtml(klass)}')">Apply</button>`;
  openModal({ title: `Adjust ${klass}`, body, footer });
}

export function commitBulkPoolAdjust(klassRaw: string): void {
  const klass = klassRaw as BulkClass;
  if (!BULK_CLASSES.includes(klass)) return;
  const inp = document.getElementById("bulkAdjustDelta") as HTMLInputElement | null;
  const delta = inp ? Number(inp.value) : NaN;
  if (!Number.isFinite(delta) || delta === 0) { toast("Enter a non-zero number.", "err"); return; }
  addToBulkPool(klass, delta);
  toast(`${klass} pool adjusted by ${delta > 0 ? "+" : ""}${delta}`);
  closeModal();
  render();
}

/* ============================================================
   Save & Add Another — save the current new card, then re-open
   the form prefilled with Set / Condition / Acq fields.
   ============================================================ */
export function saveCardAndAddAnother(): void {
  const form = $("#cardForm") as HTMLFormElement | null;
  if (!form) return;
  // Snapshot the fields we want to carry over BEFORE saveCard wipes the modal.
  const carry = readForm(form);
  saveCard("");  // empty origId → new lot
  // saveCard() closes the modal and re-renders. Re-open Add Card with prefills.
  setTimeout(() => {
    const newRow: Row = {
      "Card Lot ID":     nextId("C", "cards", "Card Lot ID"),
      "Acq Date":        carry["Acq Date"] || todayISO(),
      "Acq Type":        carry["Acq Type"] || "Purchase",
      "Inventory Class": carry["Inventory Class"] || "Raw",
      "Status":          carry["Status"] || "Available",
      "Game":            carry["Game"] || "Pokemon",
      "Set":             carry["Set"] || "",
      "Condition/Grade": carry["Condition/Grade"] || "Near Mint",
      "Qty":             carry["Qty"] || 1,
      "Unit Basis":      carry["Unit Basis"] || 0,
      "Source ID":       carry["Source ID"] || "",
      "Card Name":  "",
      "Card #":     "",
      "Current FMV": 0,
      "Image URL":  "",
      "Notes":      "",
    };
    editCardWithRow(newRow, true);
    // Focus the Card Name field so the user can immediately type "Pikachu TG05"
    // and click Lookup (or just keep typing fields by hand).
    setTimeout(() => {
      const nameField = document.querySelector<HTMLInputElement>('#cardForm [name="Card Name"]');
            if (nameField) nameField.focus();
    }, 50);
  });
}

// Inline-handler bridges
(window as any).editCard               = editCard;
(window as any).editCardWithRow        = editCardWithRow;
(window as any).saveCard               = saveCard;
(window as any).lookupCardPrice        = lookupCardPrice;
(window as any).adjustBulkPool         = adjustBulkPool;
(window as any).commitBulkPoolAdjust   = commitBulkPoolAdjust;
(window as any).saveCardAndAddAnother  = saveCardAndAddAnother;
(window as any).exportCardsReportFromView = exportCardsReportFromView;
(window as any).cardsToolbarLabels = cardsToolbarLabels;
